/**
 * The app's language and reading direction.
 *
 * Direction is applied as a `direction` style on one root view rather than
 * through `I18nManager.forceRTL`, which only takes effect after the app is
 * killed and reopened — a donor who taps اردو expects the screen to turn
 * around there and then, not next launch. Yoga mirrors the whole subtree from
 * that one property on native, and it maps to CSS `direction` on web.
 *
 * The choice is remembered, and read back before the first screen paints so
 * the app does not flash English at a donor who has already chosen Urdu.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setFormatLocale } from '@mfa/domain';
import { storage } from '../storage/storage';
import {
  CURRENCY,
  LANGUAGES,
  LANGUAGE_STORAGE_KEY,
  MONTHS,
  PEOPLE_WORDS,
  isRTLLanguage,
  otherLanguage,
  type Language,
} from './language';
import { applyNativeDirection } from './nativeDirection';
import { ur } from './ur';

const DICTIONARIES: Record<Language, Record<string, string>> = {
  en: {},
  ur,
};

export type Translate = (source: string) => string;

export interface LanguageValue {
  language: Language;
  isRTL: boolean;
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  t: Translate;
}

const LanguageContext = createContext<LanguageValue | null>(null);

function isLanguage(value: string | null): value is Language {
  return value !== null && (LANGUAGES as readonly string[]).includes(value);
}

export function LanguageProvider({
  children,
  initial = 'en',
}: {
  children: ReactNode;
  initial?: Language;
}) {
  const [language, setLanguageState] = useState<Language>(initial);
  // Whether the stored choice has been read. Until then `language` is only the
  // default, and acting on it would undo a donor's choice.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void storage
      .getItem(LANGUAGE_STORAGE_KEY)
      .then((stored) => {
        if (!cancelled && isLanguage(stored)) setLanguageState(stored);
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // At startup, bring Android's native direction in line with the stored
  // choice — once, and only once that choice is known.
  useEffect(() => {
    if (hydrated) applyNativeDirection(isRTLLanguage(language));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startup only
  }, [hydrated]);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    // On Android a change of direction restarts the app, so the write has to
    // land first — otherwise it reopens in the language it just left, sees
    // the mismatch, and restarts back.
    void storage
      .setItem(LANGUAGE_STORAGE_KEY, next)
      .then(() => applyNativeDirection(isRTLLanguage(next)));
  }, []);

  // Money, dates and headcounts are formatted deep inside shared code that
  // has no access to this context, so the words they need are handed over
  // whenever the language changes — before the render that uses them.
  setFormatLocale({
    currency: CURRENCY[language],
    months: MONTHS[language],
    ...PEOPLE_WORDS[language],
  });

  const value = useMemo<LanguageValue>(() => {
    const dictionary = DICTIONARIES[language];
    return {
      language,
      isRTL: isRTLLanguage(language),
      setLanguage,
      toggleLanguage: () => setLanguage(otherLanguage(language)),
      // An untranslated sentence reads as English rather than as a key.
      t: (source: string) => dictionary[source] ?? source,
    };
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

const FALLBACK: LanguageValue = {
  language: 'en',
  isRTL: false,
  setLanguage: () => {},
  toggleLanguage: () => {},
  t: (source) => source,
};

/**
 * Usable outside the provider — a component rendered in isolation by a test
 * should show English, not throw.
 */
export function useLanguage(): LanguageValue {
  return useContext(LanguageContext) ?? FALLBACK;
}

export function useT(): Translate {
  return useLanguage().t;
}
